import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { TNil } from '@app/app.interface';
import { CtaDetailsListService } from '@campaign-form/form-fields/creators-and-ctas/cta-details-list.service';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { CampaignAnalyticsService } from '@core/services/campaign-analytics.service';
import { EPlatformFeature } from '@enums/campaign.enum';
import {
  ICampaignAnalyticsApi,
  ICampaignAnalyticsApiResponse,
  ICreatorAnalyticsSliceApi,
  ICreatorCtaKpiStatusApi,
  IMatchedKpiAnalyticsApi,
} from '@interfaces/campaign-analytics-api.interface';
import { IAnalyticsNode, ICampaignAnalytics } from '@interfaces/campaign-analytics.interface';
import { ComponentStore } from '@ngrx/component-store';
import { TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { CampaignsService } from '@pages/campaigns/campaigns.service';
import { CampaignAnalyticsUtilitiesService } from '@utilities/campaign-analytics-utilities.service';
import { API_DATE_TIME_FORMAT_WITH_MS, DateUtilitiesService } from '@utilities/date-utilities.service';
import { format } from 'date-fns';
import { upperFirst } from 'lodash';
import { EMPTY, interval, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, mergeMap, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ICampaignExportApi } from './campaign-details-banner/campaign-export-dialog/campaign-export-dialog.interface';
import { CampaignExportService } from './campaign-details-banner/campaign-export-dialog/campaign-export.service';
import { CampaignDetailsErrorService } from './campaign-details-error.service';
import {
  ECampaignDetailsTab,
  ICampaignAnalyticsFilter,
  ICampaignDetailsQueryState,
} from './campaign-details.interface';
import { CampaignDetailsService } from './campaign-details.service';

export interface ICampaignDetailsState {
  activeTab: ECampaignDetailsTab;
  ctaKpiStatusState: ICampaignDetailsQueryState<ICreatorCtaKpiStatusApi[]>;
  campaignAnalyticsMap: Record<string, ICampaignAnalyticsApi | TNil>;
  campaignAnalyticsShell: ICampaignAnalytics | null;
  campaignAnalyticsShellNodes: IAnalyticsNode[] | null;
  currentCampaignId: string | null;
  // The filter/date-range signature the user currently wants applied - distinct from any single
  // tab's ICampaignDetailsQueryState.filterSignature, which reverts to confirmedFilterSignature on
  // a failed fetch and can therefore end up equal to a PREVIOUS desired signature even while this
  // one has moved on to something else. Exposed via currentFilterSignature$ specifically so
  // SocialContentTilesListComponent can tell those two situations apart - see that component's
  // showInitialSkeleton/createClearAwaitingFilterRefreshEffect for why the distinction matters.
  currentFilterSignature: string | null;
  campaign: TCampaignApi | null;
  campaignExport: ICampaignExportApi | null;
  campaignNotFound: boolean;
  deliverablesState: ICampaignDetailsQueryState<IAnalyticsNode[]>;
  error: string | null;
  initialCampaignLoaded: boolean;
  initialAnalyticsLoaded: boolean;
  kpiState: ICampaignDetailsQueryState<IMatchedKpiAnalyticsApi[]>;
  loading: boolean;
  isRefreshing: boolean;
  versionChanged: boolean;
  creatorAnalyticsSlices: ICreatorAnalyticsSliceApi[] | TNil;
  analyticsLoading: boolean;
  slicesLoading: boolean;
  limitReached: boolean;
}

export interface ICampaignDetailsFetchParams {
  adminMode: boolean;
  analyticsDataExists: boolean;
  briefingId: string;
  campaignId: string;
  creatorBriefingMode: boolean;
  endDate?: Date | null;
  exportId: string;
  exportMode: boolean;
  startDate?: Date | null;
}

export interface ICampaignDetailsAnalyticsFetchParams extends Omit<ICampaignDetailsFetchParams, 'analyticsDataExists'> {
  analyticsDataExists?: never;
}

const initialState: ICampaignDetailsState = {
  activeTab: ECampaignDetailsTab.overview,
  analyticsLoading: false,
  campaign: null,
  campaignAnalyticsMap: {},
  campaignAnalyticsShell: null,
  campaignAnalyticsShellNodes: null,
  campaignExport: null,
  campaignNotFound: false,
  creatorAnalyticsSlices: null,
  ctaKpiStatusState: createQueryState<ICreatorCtaKpiStatusApi[]>(),
  currentCampaignId: null,
  currentFilterSignature: null,
  deliverablesState: createQueryState<IAnalyticsNode[]>(),
  error: null,
  initialAnalyticsLoaded: false,
  initialCampaignLoaded: false,
  isRefreshing: false,
  kpiState: createQueryState<IMatchedKpiAnalyticsApi[]>(),
  limitReached: false,
  loading: false,
  slicesLoading: false,
  versionChanged: false,
};

// Refresh interval in milliseconds (3 minutes)
export const REFRESH_INTERVAL = 3 * 60 * 1000;
// Check interval in milliseconds (1 minute)
const CHECK_INTERVAL = 60 * 1000;

function createQueryState<T>(): ICampaignDetailsQueryState<T> {
  return {
    confirmedFilterSignature: null,
    data: null,
    error: null,
    filterSignature: null,
    inFlight: false,
    lastReceivedAt: null,
    requestKey: 0,
  };
}

@Injectable()
export class CampaignDetailsStore extends ComponentStore<ICampaignDetailsState> implements OnDestroy {
  public readonly activeTab$ = this.selectSignal((state: ICampaignDetailsState) => state.activeTab);
  public readonly campaignAnalyticsMap$ = this.selectSignal(
    (state: ICampaignDetailsState) => state.campaignAnalyticsMap,
  );
  public readonly campaignAnalyticsShell$ = this.selectSignal(
    (state: ICampaignDetailsState) => state.campaignAnalyticsShell,
  );
  public readonly campaignAnalyticsShellNodes$ = this.selectSignal(
    (state: ICampaignDetailsState) => state.campaignAnalyticsShellNodes,
  );
  public readonly campaignApi$ = this.selectSignal((state: ICampaignDetailsState) => state.campaign);
  public readonly campaignAnalyticsApi$ = this.selectSignal((state: ICampaignDetailsState) => {
    if (state.currentCampaignId) {
      return state.campaignAnalyticsMap[state.currentCampaignId] || null;
    }

    return null;
  });
  public readonly versionChanged$ = this.selectSignal((state: ICampaignDetailsState) => {
    return state.versionChanged;
  });
  public readonly loading$ = this.selectSignal((state: ICampaignDetailsState) => state.loading);
  public readonly error$ = this.selectSignal((state: ICampaignDetailsState) => state.error);
  public readonly initialCampaignLoaded$ = this.selectSignal(
    (state: ICampaignDetailsState) => state.initialCampaignLoaded,
  );
  public readonly initialAnalyticsLoaded$ = this.selectSignal(
    (state: ICampaignDetailsState) => state.initialAnalyticsLoaded,
  );
  public readonly campaignNotFound$ = this.selectSignal((state: ICampaignDetailsState) => state.campaignNotFound);
  public readonly campaignExport$ = this.selectSignal((state: ICampaignDetailsState) => state.campaignExport);
  public readonly ctaKpiStatusState$ = this.selectSignal((state: ICampaignDetailsState) => state.ctaKpiStatusState);
  public readonly creatorAnalyticsSlices$ = this.selectSignal(
    (state: ICampaignDetailsState) => state.creatorAnalyticsSlices,
  );
  public readonly deliverablesState$ = this.selectSignal((state: ICampaignDetailsState) => state.deliverablesState);
  public readonly currentFilterSignature$ = this.selectSignal(
    (state: ICampaignDetailsState) => state.currentFilterSignature,
  );
  public readonly kpiState$ = this.selectSignal((state: ICampaignDetailsState) => state.kpiState);
  public readonly analyticsLoading$ = this.selectSignal((state: ICampaignDetailsState) => state.analyticsLoading);
  public readonly slicesLoading$ = this.selectSignal((state: ICampaignDetailsState) => state.slicesLoading);
  public readonly limitReached$ = this.selectSignal((state: ICampaignDetailsState) => state.limitReached);
  public readonly setActiveTab = this.updater((state: ICampaignDetailsState, activeTab: ECampaignDetailsTab) => {
    return {
      ...state,
      activeTab,
    };
  });

  public readonly setCurrentCampaignId = this.updater((state: ICampaignDetailsState, campaignId: string | null) => {
    return {
      ...state,
      currentCampaignId: campaignId,
    };
  });

  public readonly setCampaign = this.updater((state: ICampaignDetailsState, campaign: TCampaignApi | null) => {
    return {
      ...state,
      campaign,
    };
  });

  public readonly setIsRefreshing = this.updater((state: ICampaignDetailsState, isRefreshing: boolean) => {
    return {
      ...state,
      isRefreshing,
    };
  });

  private refreshStop$ = new Subject<void>();
  private refreshInterval$: Subscription | null = null;
  private ignoreInitialAnalyticsFetch = false;
  private ignoreInitialAnalyticsFetchAfter404 = false;
  private lastAnalyticsReceivedAt: number | null = null;
  private lastDateRange: Pick<ICampaignAnalyticsFilter, 'endDate' | 'startDate'> | null = null;
  private tabRefreshInterval$: Subscription | null = null;
  private currentTabFetchParams: ICampaignDetailsAnalyticsFetchParams | null = null;
  private currentFilter: ICampaignAnalyticsFilter = {
    creatorIds: new Set(),
    ctaTemplateIds: new Set(),
    endDate: null,
    executionStatuses: new Set(),
    platformFeatures: new Set(),
    startDate: null,
  };
  private currentRequestKey = 0;

  public readonly startPeriodicFetching = this.effect((params$: Observable<ICampaignDetailsFetchParams>) => {
    return params$.pipe(
      tap((params) => this.initializePeriodicFetching(params)),
      switchMap((params) => this.setupPeriodicIntervalCheck(params)),
    );
  });

  public readonly initializeCampaignDetails = this.effect(
    (params$: Observable<ICampaignDetailsAnalyticsFetchParams>) => {
      return params$.pipe(
        tap((params) => {
          this.currentTabFetchParams = params;
          this.currentRequestKey = 0;
          this.tabRefreshInterval$?.unsubscribe();
          this.patchState({
            activeTab: ECampaignDetailsTab.overview,
            analyticsLoading: true,
            campaign: null,
            campaignAnalyticsShell: null,
            campaignAnalyticsShellNodes: null,
            campaignNotFound: false,
            ctaKpiStatusState: createQueryState<ICreatorCtaKpiStatusApi[]>(),
            currentFilterSignature: null,
            deliverablesState: createQueryState<IAnalyticsNode[]>(),
            error: null,
            initialAnalyticsLoaded: false,
            initialCampaignLoaded: false,
            kpiState: createQueryState<IMatchedKpiAnalyticsApi[]>(),
            loading: true,
          });
          this.setCurrentCampaignId(params.campaignId);
        }),
        switchMap((params) =>
          this.getCampaignAnalyticsFetch(params).pipe(
            tap((response) => this.handleInitialCampaignAnalyticsSuccess(params.campaignId, response)),
            switchMap(() => this.getCampaignFetch({ ...params, analyticsDataExists: true })),
            catchError((error: HttpErrorResponse) => {
              if (error.status === 404) {
                this.handleInitialCampaignAnalyticsNotFound(params.campaignId);

                return this.getCampaignFetch({ ...params, analyticsDataExists: false });
              }

              this.patchState({
                analyticsLoading: false,
                error: `Failed to load analytics for campaign: ${params.campaignId}`,
                initialAnalyticsLoaded: true,
              });

              return this.getCampaignFetch({ ...params, analyticsDataExists: false });
            }),
          ),
        ),
        tap((campaign) => {
          if (!campaign) {
            throw new Error('Campaign payload is empty');
          }

          const formattedCampaign = this.safeFormatCampaign(campaign);

          this.safeSyncCampaignShell(formattedCampaign);
          this.patchState({
            campaignNotFound: false,
            error: null,
            initialCampaignLoaded: true,
            loading: false,
          });
          this.startTabRefreshInterval();
        }),
        catchError((error: unknown) => {
          this.patchState({
            campaignNotFound: this.campaignDetailsErrorService.shouldMarkCampaignAsNotFound(error),
            error: this.campaignDetailsErrorService.buildCampaignInitializationError(error, this.currentTabFetchParams),
            loading: false,
          });

          return EMPTY;
        }),
      );
    },
  );

  public readonly updateFilter = this.effect((filter$: Observable<ICampaignAnalyticsFilter>) => {
    return filter$.pipe(
      tap((filter) => {
        this.currentFilter = filter;
        this.patchState({
          currentFilterSignature: this.campaignDetailsService.getFilterSignature(filter),
          limitReached: false,
        });
        this.fetchTabsForCurrentFilter(true);
      }),
    );
  });

  public readonly selectTabForFetch = this.effect((tab$: Observable<ECampaignDetailsTab>) => {
    return tab$.pipe(
      tap((tab) => {
        this.setActiveTab(tab);
        this.fetchActiveTabForCurrentFilter(true);
      }),
    );
  });

  public readonly fetchCampaign = this.effect((params$: Observable<ICampaignDetailsFetchParams>) => {
    return params$.pipe(
      tap(() => {
        this.patchState({ error: null, loading: true });
      }),
      mergeMap(
        ({
          campaignId,
          adminMode,
          creatorBriefingMode,
          briefingId,
          analyticsDataExists,
          exportMode,
          exportId,
          endDate,
          startDate,
        }) => {
          if (!campaignId && !creatorBriefingMode && !exportMode) {
            return EMPTY;
          }

          const fetch$ = this.getCampaignFetch({
            adminMode,
            analyticsDataExists,
            briefingId,
            campaignId,
            creatorBriefingMode,
            exportId,
            exportMode,
          });

          return fetch$.pipe(
            tap((campaign) => {
              if (campaign) {
                const formattedCampaign = this.setFormattedCampaign(campaign);
                const isLive = this.campaignDetailsService.isCampaignLive(formattedCampaign);
                const { isRefreshing } = this.get();

                if (isLive && !isRefreshing) {
                  this.setIsRefreshing(true);
                  this.startRefreshInterval({
                    adminMode,
                    analyticsDataExists,
                    briefingId,
                    campaignId,
                    creatorBriefingMode,
                    endDate,
                    exportId,
                    exportMode,
                    startDate,
                  });
                } else if (!isLive && isRefreshing) {
                  this.setIsRefreshing(false);
                  this.refreshStop$.next();
                }
              }

              this.patchState({ initialCampaignLoaded: true, loading: false });
            }),
            catchError(() => {
              this.patchState({
                campaignNotFound: true,
                error: `Failed to load campaign: ${campaignId}`,
                loading: false,
              });

              return EMPTY;
            }),
          );
        },
      ),
    );
  });

  public readonly fetchCampaignAnalytics = this.effect((params$: Observable<ICampaignDetailsAnalyticsFetchParams>) => {
    return params$.pipe(
      tap(({ campaignId }) => {
        this.setCurrentCampaignId(campaignId);
      }),
      mergeMap(({ campaignId, adminMode, creatorBriefingMode, briefingId, exportMode, exportId }) => {
        this.patchState({ analyticsLoading: true, limitReached: false, loading: true });

        const fetch$ = this.getCampaignAnalyticsFetch({
          adminMode,
          briefingId,
          campaignId,
          creatorBriefingMode,
          exportId,
          exportMode,
        });

        return fetch$.pipe(
          tap((response: ICampaignAnalyticsApiResponse) => {
            const analyticsCampaignId = campaignId || response.campaignAnalytics.id;
            this.setCurrentCampaignId(analyticsCampaignId);

            this.patchState((state) => {
              if (!this.campaignDetailsService.isVersionChanged(state, response)) {
                return {
                  analyticsLoading: false,
                  loading: false,
                  versionChanged: false,
                };
              }

              const updatedMap = {
                ...state.campaignAnalyticsMap,
                [analyticsCampaignId]: response.campaignAnalytics,
              };

              return {
                analyticsLoading: false,
                campaignAnalyticsMap: updatedMap,
                initialAnalyticsLoaded: true,
                loading: false,
                versionChanged: true,
              };
            });

            this.syncCampaignShell();
          }),
          catchError((error: HttpErrorResponse) => this.handleCampaignAnalyticsError(error, campaignId)),
        );
      }),
    );
  });

  public readonly fetchCreatorAnalyticsSlices = this.effect(
    (params$: Observable<ICampaignDetailsAnalyticsFetchParams>) => {
      return params$.pipe(
        mergeMap(
          ({ adminMode, campaignId, creatorBriefingMode, briefingId, exportMode, exportId, endDate, startDate }) => {
            const effectiveCampaignId = this.campaignDetailsService.getEffectiveAnalyticsCampaignId(this.get(), {
              campaignId,
              creatorBriefingMode,
              exportMode,
            });

            if (!effectiveCampaignId) {
              return EMPTY;
            }

            this.patchState({ loading: true, slicesLoading: true });

            this.lastDateRange = { endDate: endDate || null, startDate: startDate || null };

            const fetch$ = this.getCreatorAnalyticsSlicesFetch({
              adminMode,
              briefingId,
              campaignId: effectiveCampaignId,
              creatorBriefingMode,
              endDate,
              exportId,
              exportMode,
              startDate,
            });

            return fetch$.pipe(
              tap((response: IApiResponse<ICreatorAnalyticsSliceApi[]>) => {
                this.patchState(() => {
                  return {
                    creatorAnalyticsSlices: response.data || [],
                    loading: false,
                    slicesLoading: false,
                  };
                });
              }),
              catchError(() => {
                this.patchState(() => ({
                  error: `Failed to load slices for campaign: ${campaignId}`,
                  loading: false,
                  slicesLoading: false,
                }));

                return EMPTY;
              }),
            );
          },
        ),
      );
    },
  );

  public readonly fetchCampaignAnalyticsBulk = this.effect(
    (params$: Observable<{ ids: string[]; adminMode?: boolean; creatorMode?: boolean }>) => {
      return params$.pipe(
        mergeMap(({ ids, adminMode, creatorMode }) => {
          if (!ids || ids.length === 0) {
            return EMPTY;
          }

          this.patchState((state) => {
            const updatedMap = { ...state.campaignAnalyticsMap };
            ids.forEach((id) => {
              updatedMap[id] = undefined;
            });

            return {
              campaignAnalyticsMap: updatedMap,
              error: null,
              loading: true,
            };
          });

          const fetch$ = adminMode
            ? this.analyticsService.getBulkCampaignAnalyticsAdmin(ids)
            : creatorMode
              ? this.analyticsService.getBulkCampaignAnalyticsCreatorBriefing(ids)
              : this.analyticsService.getBulkCampaignAnalytics(ids);

          return fetch$.pipe(
            tap((responses: ICampaignAnalyticsApiResponse[]) => {
              this.patchState((state) => {
                const updatedMap = { ...state.campaignAnalyticsMap };

                responses.forEach((response) => {
                  if (response.campaignAnalytics?.id) {
                    updatedMap[response.campaignAnalytics.id] = response.campaignAnalytics;
                  }
                });

                ids.forEach((id) => {
                  if (!updatedMap[id]) {
                    updatedMap[id] = null;
                  }
                });

                return {
                  campaignAnalyticsMap: updatedMap,
                  loading: false,
                };
              });
            }),
            catchError(() => {
              this.patchState((state) => {
                const updatedMap = { ...state.campaignAnalyticsMap };
                ids.forEach((id) => {
                  updatedMap[id] = null;
                });

                return {
                  campaignAnalyticsMap: updatedMap,
                  error: `Failed to load${adminMode ? ' admin' : ''} analytics for campaigns: ${ids.join(', ')}`,
                  loading: false,
                };
              });

              return EMPTY;
            }),
          );
        }),
      );
    },
  );

  public readonly fetchCampaignExport = this.effect((params$: Observable<string>) => {
    return params$.pipe(
      tap(() => {
        this.patchState({ error: null });
      }),
      mergeMap((exportId) => {
        return this.campaignExportService.getCampaignExport(exportId, new HttpParams().set('joins', 'all')).pipe(
          tap((campaignExport) => {
            this.patchState({ campaignExport });

            if (this.get().currentFilterSignature) {
              this.fetchTabsForCurrentFilter(false);
            }
          }),
          catchError(() => {
            this.patchState({
              campaignNotFound: true,
              error: `Failed to load campaign export: ${exportId}`,
              loading: false,
            });

            return EMPTY;
          }),
        );
      }),
    );
  });

  private readonly analyticsService = inject(CampaignAnalyticsService);
  private readonly campaignsService = inject(CampaignsService);
  private readonly campaignAnalyticsUtilities = inject(CampaignAnalyticsUtilitiesService);
  private readonly campaignDetailsService = inject(CampaignDetailsService);
  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly ctaDetailsListService = inject(CtaDetailsListService);
  private readonly campaignExportService = inject(CampaignExportService);
  private readonly campaignDetailsErrorService = inject(CampaignDetailsErrorService);

  constructor() {
    super(initialState);
  }

  private initializePeriodicFetching(params: ICampaignDetailsFetchParams): void {
    const {
      campaignId,
      adminMode,
      creatorBriefingMode,
      briefingId,
      analyticsDataExists,
      exportMode,
      exportId,
      endDate,
      startDate,
    } = params;

    this.ignoreInitialAnalyticsFetch = !!analyticsDataExists;

    this.refreshStop$.next();
    this.refreshInterval$?.unsubscribe();
    this.setIsRefreshing(false);
    this.setCurrentCampaignId(campaignId);

    if (!this.ignoreInitialAnalyticsFetch && !this.ignoreInitialAnalyticsFetchAfter404) {
      this.fetchCampaignAnalytics({
        adminMode,
        briefingId,
        campaignId,
        creatorBriefingMode,
        exportId,
        exportMode,
      });
    }

    if (analyticsDataExists) {
      this.fetchCreatorAnalyticsSlices({
        adminMode,
        briefingId,
        campaignId,
        creatorBriefingMode,
        endDate: this.lastDateRange?.endDate || endDate || null,
        exportId,
        exportMode,
        startDate: this.lastDateRange?.startDate || startDate || null,
      });
    }

    this.ignoreInitialAnalyticsFetch = false;
    this.ignoreInitialAnalyticsFetchAfter404 = false;
  }

  private setupPeriodicIntervalCheck(params: ICampaignDetailsFetchParams): Observable<unknown> {
    const {
      campaignId,
      adminMode,
      creatorBriefingMode,
      briefingId,
      analyticsDataExists,
      endDate,
      exportMode,
      exportId,
      startDate,
    } = params;

    return interval(CHECK_INTERVAL).pipe(
      tap(() => {
        const { campaign, isRefreshing } = this.get();

        if (!campaign) {
          return;
        }

        const isLive = this.campaignDetailsService.isCampaignLive(campaign);

        if (isLive && !isRefreshing) {
          this.setIsRefreshing(true);
          this.startRefreshInterval({
            adminMode,
            analyticsDataExists,
            briefingId,
            campaignId,
            creatorBriefingMode,
            endDate,
            exportId,
            exportMode,
            startDate,
          });
        } else if (!isLive && isRefreshing) {
          this.setIsRefreshing(false);
          this.refreshStop$.next();
        }
      }),
    );
  }

  private handleInitialCampaignAnalyticsSuccess(campaignId: string, response: ICampaignAnalyticsApiResponse): void {
    this.setCurrentCampaignId(campaignId || response.campaignAnalytics.id);
    this.lastAnalyticsReceivedAt = Date.now();
    this.patchState((state) => ({
      analyticsLoading: false,
      campaignAnalyticsMap: {
        ...state.campaignAnalyticsMap,
        [campaignId || response.campaignAnalytics.id]: response.campaignAnalytics,
      },
      initialAnalyticsLoaded: true,
    }));
    this.syncCampaignShell();
  }

  private handleInitialCampaignAnalyticsNotFound(campaignId: string): void {
    this.patchState((state) => ({
      analyticsLoading: false,
      campaignAnalyticsMap: {
        ...state.campaignAnalyticsMap,
        [campaignId]: null,
      },
      initialAnalyticsLoaded: true,
    }));
    this.syncCampaignShell();
  }

  // fetchCampaignAnalytics runs both as the initial analytics load AND as a periodic BACKGROUND
  // refresh for an already-loaded, live campaign (see startRefreshInterval, which calls it every
  // REFRESH_INTERVAL) - campaignAnalyticsApi$ and fetchDeliverablesTab's "no analytics" shortcut
  // both read campaignAnalyticsMap[campaignId] directly, so only a genuine 404 should actually
  // overwrite that entry with null. Any other error here (a transient network blip, a 500, a
  // timeout) must leave an existing real entry untouched: nulling it on a merely transient
  // failure previously made the deliverables tab silently confirm an empty result on its very
  // next fetch, instead of keeping the last known-good data on screen and retrying once the next
  // periodic refresh actually succeeds.
  private handleCampaignAnalyticsError(error: HttpErrorResponse, campaignId: string): Observable<never> {
    const is404 = error.status === 404;

    if (is404 && !this.ignoreInitialAnalyticsFetchAfter404) {
      this.ignoreInitialAnalyticsFetchAfter404 = true;
    }

    this.patchState((state) => ({
      analyticsLoading: false,
      campaignAnalyticsMap: is404 ? { ...state.campaignAnalyticsMap, [campaignId]: null } : state.campaignAnalyticsMap,
      error: `Failed to load analytics for campaign: ${campaignId}`,
      initialAnalyticsLoaded: true,
      limitReached: this.campaignDetailsErrorService.getLimitReached(error),
      loading: false,
      versionChanged: true,
    }));

    return EMPTY;
  }

  private startTabRefreshInterval(): void {
    this.tabRefreshInterval$?.unsubscribe();
    this.tabRefreshInterval$ = interval(CHECK_INTERVAL).subscribe(() => {
      this.refreshStaleTabs();
    });
  }

  private refreshStaleTabs(): void {
    this.fetchTabsForCurrentFilter(false, true);
    this.refreshCampaignAnalyticsIfStale();
  }

  private refreshCampaignAnalyticsIfStale(): void {
    if (!this.currentTabFetchParams || !this.isExpired(this.lastAnalyticsReceivedAt)) {
      return;
    }

    this.getCampaignAnalyticsFetch(this.currentTabFetchParams)
      .pipe(catchError(() => EMPTY))
      .subscribe((response: ICampaignAnalyticsApiResponse) => {
        const campaignId = this.currentTabFetchParams?.campaignId ?? response.campaignAnalytics.id;

        this.lastAnalyticsReceivedAt = Date.now();
        this.patchState((state) => ({
          campaignAnalyticsMap: {
            ...state.campaignAnalyticsMap,
            [campaignId]: response.campaignAnalytics,
          },
        }));
        this.syncCampaignShell();
      });
  }

  private fetchActiveTabForCurrentFilter(refreshStaleOnly = false): void {
    const { activeTab, currentFilterSignature } = this.get();

    if (!currentFilterSignature || !this.hasResolvedAnalyticsCampaignId()) {
      return;
    }

    if (this.currentTabFetchParams) {
      if (this.currentTabFetchParams.exportMode && this.get().campaignExport === null) {
        return;
      }

      const visibleTabs = this.campaignDetailsService.getVisibleTabs(
        this.currentTabFetchParams,
        this.getEnabledTabsForExport(),
      );

      if (
        visibleTabs.length === 0 ||
        (!this.currentTabFetchParams?.creatorBriefingMode && !visibleTabs.includes(activeTab))
      ) {
        return;
      }
    }

    this.fetchTabIfNeeded(activeTab, this.currentRequestKey, currentFilterSignature, false, refreshStaleOnly);
  }

  private fetchTabsForCurrentFilter(invalidateCurrentRequests: boolean, refreshStaleOnly = false): void {
    const { activeTab, currentFilterSignature } = this.get();

    if (
      !currentFilterSignature ||
      !this.currentTabFetchParams ||
      !this.get().campaignAnalyticsShell ||
      !this.hasResolvedAnalyticsCampaignId()
    ) {
      return;
    }

    if (this.currentTabFetchParams.exportMode && this.get().campaignExport === null) {
      return;
    }

    if (invalidateCurrentRequests) {
      this.incrementRequestKey();
    }

    const visibleTabs = this.campaignDetailsService.getVisibleTabs(
      this.currentTabFetchParams,
      this.getEnabledTabsForExport(),
    );

    if (visibleTabs.length === 0) {
      return;
    }

    const backgroundTabs = visibleTabs.filter((tab) => tab !== activeTab);

    if (visibleTabs.includes(activeTab)) {
      this.fetchTabIfNeeded(
        activeTab,
        this.currentRequestKey,
        currentFilterSignature,
        invalidateCurrentRequests,
        refreshStaleOnly,
      );
    }

    backgroundTabs.forEach((tab) => {
      this.fetchTabIfNeeded(
        tab,
        this.currentRequestKey,
        currentFilterSignature,
        invalidateCurrentRequests,
        refreshStaleOnly,
      );
    });
  }

  private getEnabledTabsForExport(): Set<ECampaignDetailsTab> | undefined {
    const { campaignExport } = this.get();

    return campaignExport && campaignExport.allTabsSelected === false && campaignExport.tabs !== null
      ? new Set(campaignExport.tabs.map((t) => t.tab))
      : undefined;
  }

  private fetchTabIfNeeded(
    tab: ECampaignDetailsTab,
    requestKey: number,
    filterSignature: string,
    forceFetch: boolean,
    refreshStaleOnly: boolean,
  ): void {
    const queryState = this.getQueryStateForTab(tab);

    if (queryState.inFlight && queryState.requestKey === requestKey && queryState.filterSignature === filterSignature) {
      return;
    }

    if (!forceFetch && !this.shouldFetchTab(tab, filterSignature, refreshStaleOnly)) {
      return;
    }

    this.fetchTab(tab, requestKey, filterSignature);
  }

  private getQueryStateForTab(
    tab: ECampaignDetailsTab,
  ):
    | ICampaignDetailsQueryState<ICreatorCtaKpiStatusApi[]>
    | ICampaignDetailsQueryState<IAnalyticsNode[]>
    | ICampaignDetailsQueryState<IMatchedKpiAnalyticsApi[]> {
    const state = this.get();

    switch (tab) {
      case ECampaignDetailsTab.contents:
      case ECampaignDetailsTab.creatorOverview:
      case ECampaignDetailsTab.creatorTimeline:
      case ECampaignDetailsTab.deliverables:
      case ECampaignDetailsTab.kpiGoals:
      case ECampaignDetailsTab.observations:
        return state.deliverablesState;
      case ECampaignDetailsTab.overview:
      case ECampaignDetailsTab.creators:
        return state.ctaKpiStatusState;
      case ECampaignDetailsTab.performance:
        return state.kpiState;
      default:
        return state.ctaKpiStatusState;
    }
  }

  private shouldFetchTab(tab: ECampaignDetailsTab, filterSignature: string, refreshStaleOnly: boolean): boolean {
    const state = this.get();

    switch (tab) {
      case ECampaignDetailsTab.contents:
      case ECampaignDetailsTab.creatorOverview:
      case ECampaignDetailsTab.creatorTimeline:
      case ECampaignDetailsTab.deliverables:
      case ECampaignDetailsTab.kpiGoals:
      case ECampaignDetailsTab.observations:
        return this.isQueryStateFetchRequired(state.deliverablesState, filterSignature, refreshStaleOnly);
      case ECampaignDetailsTab.overview:
        return this.isQueryStateFetchRequired(state.ctaKpiStatusState, filterSignature, refreshStaleOnly);
      case ECampaignDetailsTab.creators:
        return this.isQueryStateFetchRequired(state.ctaKpiStatusState, filterSignature, refreshStaleOnly);
      case ECampaignDetailsTab.performance:
        return this.isQueryStateFetchRequired(state.kpiState, filterSignature, refreshStaleOnly);
      default:
        return false;
    }
  }

  private isQueryStateFetchRequired<T>(
    state: ICampaignDetailsQueryState<T>,
    filterSignature: string,
    refreshStaleOnly: boolean,
  ): boolean {
    if (state.inFlight) {
      return false;
    }

    if (!state.data || state.filterSignature !== filterSignature) {
      return true;
    }

    return refreshStaleOnly ? this.isExpired(state.lastReceivedAt) : false;
  }

  private isExpired(lastReceivedAt: number | null): boolean {
    return lastReceivedAt === null || Date.now() - lastReceivedAt >= REFRESH_INTERVAL;
  }

  private fetchTab(tab: ECampaignDetailsTab, requestKey: number, filterSignature: string): void {
    switch (tab) {
      case ECampaignDetailsTab.contents:
      case ECampaignDetailsTab.creatorOverview:
      case ECampaignDetailsTab.creatorTimeline:
      case ECampaignDetailsTab.kpiGoals:
      case ECampaignDetailsTab.observations:
        this.fetchDeliverablesTab(requestKey, filterSignature);
        break;
      case ECampaignDetailsTab.overview:
        this.fetchCtaKpiStatusTab(requestKey, filterSignature);
        break;
      case ECampaignDetailsTab.performance:
        this.fetchKpiTab(requestKey, filterSignature);
        break;
      case ECampaignDetailsTab.deliverables:
        this.fetchDeliverablesTab(requestKey, filterSignature);
        break;
      case ECampaignDetailsTab.creators:
        this.fetchCtaKpiStatusTab(requestKey, filterSignature);
        break;
      default:
        break;
    }
  }

  private fetchCtaKpiStatusTab(requestKey: number, filterSignature: string): void {
    if (!this.hasResolvedAnalyticsCampaignId()) {
      return;
    }

    this.patchState((state) => ({
      ...state,
      ctaKpiStatusState: this.startQueryState(state.ctaKpiStatusState, requestKey, filterSignature),
    }));

    this.getCtaKpiStatusRequest(this.buildFilteredHttpParams())
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleTabRequestError(error, 'ctaKpiStatusState', requestKey, filterSignature),
        ),
      )
      .subscribe((response: ICreatorCtaKpiStatusApi[] | null) => {
        if (
          !response ||
          !this.campaignDetailsService.isCurrentQueryStateRequest(
            this.get().ctaKpiStatusState,
            requestKey,
            filterSignature,
          )
        ) {
          return;
        }

        this.patchState((state) => ({
          ...state,
          ctaKpiStatusState: {
            ...state.ctaKpiStatusState,
            confirmedFilterSignature: filterSignature,
            data: response,
            error: null,
            filterSignature,
            inFlight: false,
            lastReceivedAt: Date.now(),
            requestKey,
          },
          limitReached: false,
        }));
      });
  }

  private fetchKpiTab(requestKey: number, filterSignature: string): void {
    if (!this.hasResolvedAnalyticsCampaignId()) {
      return;
    }

    this.patchState((state) => ({
      ...state,
      kpiState: this.startQueryState(state.kpiState, requestKey, filterSignature),
    }));

    this.getMatchedKpisRequest(this.buildFilteredHttpParams())
      .pipe(catchError((error: HttpErrorResponse) => this.handleKpiRequestError(error, requestKey, filterSignature)))
      .subscribe((response: IMatchedKpiAnalyticsApi[] | null) => {
        if (
          !response ||
          !this.campaignDetailsService.isCurrentQueryStateRequest(this.get().kpiState, requestKey, filterSignature)
        ) {
          return;
        }

        this.patchState((state) => ({
          ...state,
          kpiState: {
            ...state.kpiState,
            confirmedFilterSignature: filterSignature,
            data: response,
            error: null,
            filterSignature,
            inFlight: false,
            lastReceivedAt: Date.now(),
            requestKey,
          },
          limitReached: false,
        }));
      });
  }

  private fetchDeliverablesTab(requestKey: number, filterSignature: string): void {
    const { campaign, campaignAnalyticsShell } = this.get();
    const campaignAnalyticsApi = this.campaignAnalyticsApi$();

    if (!campaign || !campaignAnalyticsShell || !this.hasResolvedAnalyticsCampaignId()) {
      return;
    }

    this.patchState((state) => ({
      ...state,
      deliverablesState: this.startQueryState(state.deliverablesState, requestKey, filterSignature),
    }));

    if (!campaignAnalyticsApi) {
      this.resolveDeliverablesTab(campaignAnalyticsShell, [], requestKey, filterSignature);

      return;
    }

    this.getCreatorAnalyticsSlicesRequest(this.buildFilteredHttpParams(undefined, true))
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleTabRequestError(error, 'deliverablesState', requestKey, filterSignature),
        ),
      )
      .subscribe((response: IApiResponse<ICreatorAnalyticsSliceApi[]> | null) => {
        if (
          !response ||
          !this.campaignDetailsService.isCurrentQueryStateRequest(
            this.get().deliverablesState,
            requestKey,
            filterSignature,
          )
        ) {
          return;
        }

        this.resolveDeliverablesTab(campaignAnalyticsShell, response.data ?? [], requestKey, filterSignature);
      });
  }

  // Shared by both fetchDeliverablesTab branches (the "no analytics yet" shortcut and the real
  // fetch's success handler) - calculateAnalyticsNodes is synchronous processing, not part of the
  // observable chain, so a throw here needs its own try/catch rather than relying on catchError
  // (see handleTabProcessingError's comment for why that distinction matters).
  private resolveDeliverablesTab(
    campaignAnalyticsShell: ICampaignAnalytics,
    slices: ICreatorAnalyticsSliceApi[],
    requestKey: number,
    filterSignature: string,
  ): void {
    try {
      const analyticsNodes = this.campaignAnalyticsUtilities.calculateAnalyticsNodes(campaignAnalyticsShell, slices);

      this.patchState((state) => ({
        ...state,
        deliverablesState: {
          ...state.deliverablesState,
          confirmedFilterSignature: filterSignature,
          data: analyticsNodes,
          error: null,
          filterSignature,
          inFlight: false,
          lastReceivedAt: Date.now(),
          requestKey,
        },
        limitReached: false,
      }));
    } catch (error) {
      this.handleTabProcessingError(error, 'deliverablesState', requestKey, filterSignature);
    }
  }

  private getCreatorAnalyticsSlicesRequest(params: HttpParams): Observable<IApiResponse<ICreatorAnalyticsSliceApi[]>> {
    if (!this.currentTabFetchParams) {
      return EMPTY;
    }

    const { adminMode, creatorBriefingMode, briefingId, exportId, exportMode } = this.currentTabFetchParams;

    return adminMode
      ? this.analyticsService.getCreatorAnalyticsSlicesAdmin(params)
      : creatorBriefingMode
        ? this.analyticsService.getCreatorAnalyticsSlicesCreatorBriefing(briefingId, params)
        : exportMode
          ? this.analyticsService.getCreatorAnalyticsSlicesExport(exportId, params)
          : this.analyticsService.getCreatorAnalyticsSlices(params);
  }

  private getCtaKpiStatusRequest(params: HttpParams): Observable<ICreatorCtaKpiStatusApi[]> {
    if (!this.currentTabFetchParams) {
      return EMPTY;
    }

    const { creatorBriefingMode, briefingId, exportId, exportMode } = this.currentTabFetchParams;

    return creatorBriefingMode
      ? this.analyticsService.getCreatorAnalyticsSlicesCtaKpiStatusCreatorBriefing(briefingId, params)
      : exportMode
        ? this.analyticsService.getCreatorAnalyticsSlicesCtaKpiStatusExport(exportId, params)
        : this.analyticsService.getCreatorAnalyticsSlicesCtaKpiStatus(params);
  }

  private getMatchedKpisRequest(params: HttpParams): Observable<IMatchedKpiAnalyticsApi[]> {
    if (!this.currentTabFetchParams) {
      return EMPTY;
    }

    const { exportId, exportMode } = this.currentTabFetchParams;

    return exportMode
      ? this.analyticsService.getCreatorAnalyticsSlicesMatchedKpisExport(exportId, params)
      : this.analyticsService.getCreatorAnalyticsSlicesMatchedKpis(params);
  }

  private buildFilteredHttpParams(
    platformFeatures?: EPlatformFeature[],
    includeSlicesDefaults = false,
    joins?: string,
  ): HttpParams {
    const effectiveCampaignId = this.campaignDetailsService.getEffectiveAnalyticsCampaignId(
      this.get(),
      this.currentTabFetchParams,
    );

    if (!this.currentTabFetchParams || !effectiveCampaignId) {
      return new HttpParams();
    }

    let params = new HttpParams().set('campaign_id', effectiveCampaignId);
    const { creatorIds, endDate, platformFeatures: selectedPlatformFeatures, startDate } = this.currentFilter;
    const effectivePlatformFeatures = platformFeatures ?? Array.from(selectedPlatformFeatures);

    if (joins) {
      params = params.set('joins', joins);
    }

    if (includeSlicesDefaults) {
      params = params.set('joins', 'social_contents.social_content').set('limit', '1000');
    }

    if (startDate) {
      params = params.set('start__gte', format(startDate, API_DATE_TIME_FORMAT_WITH_MS));
    }

    if (endDate) {
      params = params.set('end__lte', format(endDate, API_DATE_TIME_FORMAT_WITH_MS));
    }

    if (creatorIds.size > 0) {
      params = params.set('creator_id__in', Array.from(creatorIds).join(','));
    }

    if (effectivePlatformFeatures.length > 0) {
      params = params.set(
        'platform_feature__in',
        effectivePlatformFeatures.map((platformFeature) => upperFirst(platformFeature)).join(','),
      );
    }

    return params;
  }

  private hasResolvedAnalyticsCampaignId(
    params: Pick<
      ICampaignDetailsAnalyticsFetchParams,
      'campaignId' | 'creatorBriefingMode' | 'exportMode'
    > | null = this.currentTabFetchParams,
  ): boolean {
    return this.campaignDetailsService.getEffectiveAnalyticsCampaignId(this.get(), params) !== null;
  }

  private startQueryState<T>(
    state: ICampaignDetailsQueryState<T>,
    requestKey: number,
    filterSignature: string,
  ): ICampaignDetailsQueryState<T> {
    return {
      ...state,
      error: null,
      filterSignature,
      inFlight: true,
      requestKey,
    };
  }

  // Reverting filterSignature to queryState.confirmedFilterSignature (rather than the NEW,
  // now-failed filterSignature that startQueryState() optimistically wrote in) means a failed
  // filter/date-range change leaves the state internally consistent with what's still on screen
  // (the old data), rather than claiming the new filter's data is present when it never arrived -
  // which previously caused isQueryStateFetchRequired to consider the tab already up to date for
  // the new filter (data non-null, filterSignature already matching) and permanently skip
  // retrying it, even on the next periodic poll.
  //
  // confirmedFilterSignature is read fresh from the CURRENT state here (inside patchState),
  // not captured by the caller before the request started - it's set exclusively in each fetch
  // method's success branch, so it always reflects the last filter that genuinely got data back,
  // no matter how many other fetches for this tab started and failed in between. That closes the
  // narrower race an earlier version of this fix still had: capturing "the previous
  // filterSignature" once at fetch-start time could itself be a not-yet-confirmed, in-flight
  // value if a second filter change (A -> B -> C) started before the first one's error arrived -
  // reverting to confirmedFilterSignature instead means every revert lands on the last known-good
  // filter regardless of how many overlapping requests are in play.
  //
  // NOTE: this does NOT by itself stop SocialContentTilesListComponent's
  // showInitialSkeleton()/awaitingFilterRefresh from clearing on this same error and silently
  // rendering the stale tiles for this one refresh cycle - that symptom is tracked and fixed
  // separately (see that component's fetch-failure-aware skeleton/error handling).
  private handleTabRequestError<T extends 'ctaKpiStatusState' | 'deliverablesState'>(
    error: HttpErrorResponse,
    stateKey: T,
    requestKey: number,
    filterSignature: string,
  ): Observable<null> {
    // Was previously silent - a fetch failure for a tab left no trace anywhere, which made a
    // (since-fixed) stuck-skeleton bug elsewhere much harder to diagnose than it needed to be.
    // eslint-disable-next-line no-console
    console.error(`[${stateKey}] request failed`, error);

    this.patchState((state) => {
      const queryState = state[stateKey];

      if (queryState.requestKey !== requestKey || queryState.filterSignature !== filterSignature) {
        return state;
      }

      return {
        ...state,
        limitReached: this.campaignDetailsErrorService.getLimitReached(error),
        [stateKey]: {
          ...queryState,
          error: error.message,
          filterSignature: queryState.confirmedFilterSignature,
          inFlight: false,
        },
      };
    });

    return of(null);
  }

  // Covers the gap handleTabRequestError can't: catchError only sees errors emitted through the
  // observable's error channel, but the success branch of each fetch method also runs synchronous
  // processing (calculateAnalyticsNodes for deliverablesState) AFTER the response arrives. A throw
  // there happens inside the subscribe() next callback, which catchError never sees at all - so
  // without this, an unexpected shape in the response (or a bug in the processing step itself)
  // would leave inFlight stuck true forever, with isQueryStateFetchRequired then permanently
  // treating the tab as already "in flight" and refusing to retry - the exact stuck-loading-
  // spinner failure mode. Call this from a try/catch around that processing step, same
  // requestKey/filterSignature staleness guard as handleTabRequestError so a slow, now-superseded
  // failure can't clobber a newer request's state.
  private handleTabProcessingError<T extends 'ctaKpiStatusState' | 'deliverablesState'>(
    error: unknown,
    stateKey: T,
    requestKey: number,
    filterSignature: string,
  ): void {
    // Was previously silent - see handleTabRequestError's comment above for why that's worth
    // fixing on its own regardless of what caused this particular error.
    // eslint-disable-next-line no-console
    console.error(`[${stateKey}] processing failed`, error);

    this.patchState((state) => {
      const queryState = state[stateKey];

      if (queryState.requestKey !== requestKey || queryState.filterSignature !== filterSignature) {
        return state;
      }

      return {
        ...state,
        [stateKey]: {
          ...queryState,
          error: error instanceof Error ? error.message : 'Failed to process analytics data',
          filterSignature: queryState.confirmedFilterSignature,
          inFlight: false,
        },
      };
    });
  }

  // See handleTabRequestError's confirmedFilterSignature comment - same reasoning applies here.
  private handleKpiRequestError(
    error: HttpErrorResponse,
    requestKey: number,
    filterSignature: string,
  ): Observable<null> {
    this.patchState((state) => {
      const queryState = state.kpiState;

      if (queryState.requestKey !== requestKey || queryState.filterSignature !== filterSignature) {
        return state;
      }

      return {
        ...state,
        kpiState: {
          ...queryState,
          error: error.message,
          filterSignature: queryState.confirmedFilterSignature,
          inFlight: false,
        },
        limitReached: this.campaignDetailsErrorService.getLimitReached(error),
      };
    });

    return of(null);
  }

  private syncCampaignShell(campaign = this.get().campaign): void {
    if (!campaign) {
      return;
    }

    const state = this.get();
    const campaignAnalytics = state.currentCampaignId
      ? (state.campaignAnalyticsMap[state.currentCampaignId] ?? null)
      : null;
    const [campaignAnalyticsShell, campaignAnalyticsShellNodes] =
      this.campaignDetailsService.getCampaignDetailsTableData(campaign, campaignAnalytics);

    this.patchState({
      campaignAnalyticsShell: structuredClone(campaignAnalyticsShell),
      campaignAnalyticsShellNodes: structuredClone(campaignAnalyticsShellNodes),
    });
  }

  private incrementRequestKey(): void {
    this.currentRequestKey += 1;
  }

  private getCampaignAnalyticsFetch(
    params: ICampaignDetailsAnalyticsFetchParams,
  ): Observable<ICampaignAnalyticsApiResponse> {
    const { adminMode, campaignId, creatorBriefingMode, briefingId, exportMode, exportId } = params;
    const httpParams = new HttpParams().set('joins', 'creator_analytics');

    return adminMode
      ? this.analyticsService.getCampaignAnalyticsAdmin(campaignId, httpParams)
      : creatorBriefingMode
        ? this.analyticsService.getCreatorBriefingCampaignAnalytics(briefingId, httpParams)
        : exportMode
          ? this.analyticsService.getExportCampaignAnalytics(exportId, httpParams)
          : this.analyticsService.getCampaignAnalytics(campaignId, httpParams);
  }

  private getCreatorAnalyticsSlicesFetch(
    params: ICampaignDetailsAnalyticsFetchParams,
  ): Observable<IApiResponse<ICreatorAnalyticsSliceApi[]>> {
    const { adminMode, campaignId, creatorBriefingMode, briefingId, endDate, exportMode, exportId, startDate } = params;
    let httpParams = new HttpParams()
      .set('joins', 'social_contents.social_content')
      .set('campaign_id', campaignId)
      .set('limit', '1000');

    if (startDate) {
      httpParams = httpParams.set('start__gte', format(startDate, API_DATE_TIME_FORMAT_WITH_MS)!);
    }

    if (endDate) {
      httpParams = httpParams.set('end__lte', format(endDate, API_DATE_TIME_FORMAT_WITH_MS)!);
    }

    return adminMode
      ? this.analyticsService.getCreatorAnalyticsSlicesAdmin(httpParams)
      : creatorBriefingMode
        ? this.analyticsService.getCreatorAnalyticsSlicesCreatorBriefing(briefingId, httpParams)
        : exportMode
          ? this.analyticsService.getCreatorAnalyticsSlicesExport(exportId, httpParams)
          : this.analyticsService.getCreatorAnalyticsSlices(httpParams);
  }

  private getCampaignFetch(params: ICampaignDetailsFetchParams): Observable<TCampaignApi | undefined> {
    const { analyticsDataExists, adminMode, campaignId, creatorBriefingMode, briefingId, exportMode, exportId } =
      params;

    let briefingParams = new HttpParams();
    let campaignParams = new HttpParams();

    if (analyticsDataExists) {
      const joins =
        'customer,campaign_creators,campaign_creators.creator,campaign_creators.creator.channels,creator_campaign_exports';
      campaignParams = campaignParams.set('joins', joins);
      briefingParams = briefingParams.set('only_creator_data', 'True');
    } else {
      campaignParams = campaignParams.set('joins', 'all');
    }

    const fetch$ = adminMode
      ? this.campaignsService.getAdminCampaign(campaignId, campaignParams)
      : creatorBriefingMode
        ? this.campaignsService.getCreatorBriefingCampaign(briefingId, briefingParams)
        : exportMode
          ? this.campaignsService.getExportCampaign(exportId, campaignParams)
          : this.campaignsService.getCampaign(campaignId, campaignParams);

    return fetch$;
  }

  public override ngOnDestroy(): void {
    this.refreshStop$.next();
    this.refreshStop$.complete();
    this.refreshInterval$?.unsubscribe();
    this.tabRefreshInterval$?.unsubscribe();
  }

  private startRefreshInterval(params: ICampaignDetailsFetchParams): void {
    const {
      adminMode,
      analyticsDataExists,
      briefingId,
      campaignId,
      creatorBriefingMode,
      endDate,
      exportMode,
      exportId,
      startDate,
    } = params;
    this.refreshInterval$ = interval(REFRESH_INTERVAL)
      .pipe(
        takeUntil(this.refreshStop$),
        tap(() => {
          this.fetchCampaign({
            adminMode,
            analyticsDataExists,
            briefingId,
            campaignId,
            creatorBriefingMode,
            endDate,
            exportId,
            exportMode,
            startDate,
          });
          this.fetchCampaignAnalytics({ adminMode, briefingId, campaignId, creatorBriefingMode, exportId, exportMode });
          this.fetchCreatorAnalyticsSlices({
            adminMode,
            briefingId,
            campaignId,
            creatorBriefingMode,
            endDate: this.lastDateRange?.endDate || endDate || null,
            exportId,
            exportMode,
            startDate: this.lastDateRange?.startDate || startDate || null,
          });
        }),
      )
      .subscribe();
  }

  private setFormattedCampaign(campaign: TCampaignApi): TCampaignApi {
    const formattedCampaign = {
      ...campaign,
      ctaTemplates: this.ctaDetailsListService.orderCtaTemplates(campaign.ctaTemplates || []),
      end: this.dateUtils.parseDateTimeFromApi(campaign.end as string),
      originalEnd: campaign.end as string,
      originalStart: campaign.start as string,
      start: this.dateUtils.parseDateTimeFromApi(campaign.start as string),
    };

    this.setCampaign(formattedCampaign);

    return formattedCampaign;
  }

  private safeFormatCampaign(campaign: TCampaignApi): TCampaignApi {
    try {
      return this.setFormattedCampaign(campaign);
    } catch (error: unknown) {
      throw new Error(`setFormattedCampaign failed: ${this.campaignDetailsErrorService.getReadableError(error)}`);
    }
  }

  private safeSyncCampaignShell(campaign: TCampaignApi): void {
    try {
      this.syncCampaignShell(campaign);
    } catch (error: unknown) {
      throw new Error(`syncCampaignShell failed: ${this.campaignDetailsErrorService.getReadableError(error)}`);
    }
  }
}
